/**
 * BattleTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip, palCardUrl } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function BattleTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [pals, setPals] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [mode, setMode] = useState("wild");          // "wild" | "trainer"
  const [palName, setPalName] = useState("");
  const [trainerId, setTrainerId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [battle, setBattle] = useState(null);        // active session
  const [cdExpiresMs, setCdExpiresMs] = useState(null);
  const [now, setNow] = useState(Date.now());
  // First-time trainer defeat → fires the gold "Achievement Unlocked" banner.
  const [achievement, setAchievement] = useState(null);
  // Mastered all 9 trainers → fires the once-in-a-game super cinematic.
  const [masterUnlocked, setMasterUnlocked] = useState(false);
  const logRef = useRef(null);

  // Load roster + trainer roster once.
  useEffect(() => {
    api.get("/palgame/box").then(({ data }) => {
      setPals(data.pals || []);
      // Auto-pick the highest-level pal so the user can dive in faster.
      const best = (data.pals || []).slice().sort((a, b) => (b.level || 0) - (a.level || 0))[0];
      if (best) setPalName((cur) => cur || best.pal_name);
    });
    api.get("/palgame/battle/trainers").then(({ data }) => {
      setTrainers(data.trainers || []);
    }).catch(() => {});
    const t = setInterval(() => setNow(Date.now()), 1000);
    const onCdUpdate = () => setCdExpiresMs(null);
    window.addEventListener("chillets:cooldown-update", onCdUpdate);
    return () => {
      clearInterval(t);
      window.removeEventListener("chillets:cooldown-update", onCdUpdate);
    };
  }, []);

  // Auto-scroll combat log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [battle?.log?.length]);

  const cdRemainMs = cdExpiresMs ? Math.max(0, cdExpiresMs - now) : 0;
  const cdMin = Math.floor(cdRemainMs / 60000);
  const cdSec = Math.floor((cdRemainMs % 60000) / 1000);

  const startBattle = async () => {
    if (!palName) return toast.error("Pick one of your pals first.");
    if (mode === "trainer" && !trainerId) return toast.error("Pick a trainer.");
    setBusy(true);
    try {
      const body = { pal_name: palName };
      if (mode === "trainer") body.trainer_id = trainerId;
      const { data } = await api.post("/palgame/battle", body);
      setBattle({ ...data, log: [], finished: false, result: null });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.expires_at) setCdExpiresMs(new Date(detail.expires_at).getTime());
      toast.error(detail?.message || apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const useSkill = async (idx) => {
    if (!battle || busy || battle.finished) return;
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/battle/action", {
        session_id: battle.session_id,
        skill_index: idx,
      });
      setBattle((prev) => ({
        ...prev,
        player: { ...prev.player, hp: data.player?.hp ?? prev.player.hp },
        // Finish payloads send `opponent` as a NAME string and the HP in
        // `opponent_state` — always prefer the object with an hp field.
        opponent: {
          ...prev.opponent,
          hp: data.opponent_state?.hp ?? (typeof data.opponent === "object" ? data.opponent?.hp : undefined) ?? prev.opponent.hp,
        },
        log: [...(prev.log || []), ...(data.events || [])],
        finished: !!data.finished,
        result: data.finished ? data : null,
      }));
      if (data.finished) {
        if (data.won) {
          firePalgamePayout(
            data.reward || 0,
            data.mode === "trainer" ? `DEFEATED ${data.trainer_name || "TRAINER"}` : "BATTLE WIN",
          );
          toast.success(data.mode === "trainer" ? "Trainer defeated!" : "Victory!");
          // First-ever defeat of this NPC → milestone cinematic.
          if (data.first_trainer_defeat) {
            // Look up the portrait from our cached trainer list so the
            // banner has the trainer's signature pal art.
            const tInfo = trainers.find((t) => t.id === data.trainer_id);
            const portrait = tInfo ? palCardUrl(tInfo.pal) : null;
            setAchievement({
              title: data.trainer_name || "Trainer Defeated",
              subtitle: "First Victory · Trainer Defeated",
              portrait,
            });
          }
          // Mastered all 9 trainers — once-in-a-game cinematic. Delay
          // the trigger so the achievement banner gets its full reveal
          // first (~3.6s), then the master cinematic takes over.
          if (data.master_trainer_unlocked) {
            setTimeout(() => setMasterUnlocked(true), 3800);
          }
        } else {
          firePalgameDefeat(0, data.mode === "trainer" ? "TRAINER DEFEAT" : "BATTLE DEFEAT");
          toast.error("Your pal was defeated.");
        }
        setCdExpiresMs(data.cooldown_expires ? new Date(data.cooldown_expires).getTime() : null);
        refreshProfile?.();
      }
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  const exitBattle = () => {
    setBattle(null);
    // Refresh trainer list — new pal levels may unlock the next tier.
    api.get("/palgame/battle/trainers").then(({ data }) => {
      setTrainers(data.trainers || []);
    }).catch(() => {});
  };

  // ---- Battle view --------------------------------------------------------
  if (battle) {
    return (
      <>
        <BattleArena
          battle={battle}
          busy={busy}
          onSkill={useSkill}
          onExit={exitBattle}
          logRef={logRef}
        />
        <AchievementUnlocked
          show={achievement !== null}
          title={achievement?.title}
          subtitle={achievement?.subtitle}
          portrait={achievement?.portrait}
          onDone={() => setAchievement(null)}
        />
        <MasterTrainerCinematic
          show={masterUnlocked}
          portraits={trainers.map((t) => palCardUrl(t.pal))}
          onDone={() => setMasterUnlocked(false)}
        />
      </>
    );
  }

  // ---- Lobby view ---------------------------------------------------------
  return (
    <div className="grid lg:grid-cols-2 gap-6 fade-up" data-testid="battle-tab">
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="label">Engage in Battle</h3>
          <div className="flex gap-1 text-[11px]" data-testid="battle-mode-toggle">
            <button
              onClick={() => setMode("wild")}
              className={`px-3 py-1.5 rounded uppercase tracking-wider border ${
                mode === "wild"
                  ? "border-[var(--teal)] text-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
                  : "border-white/10 text-[var(--muted)] hover:text-[var(--bone)]"
              }`}
              data-testid="battle-mode-wild"
            >
              Wild Pal
            </button>
            <button
              onClick={() => setMode("trainer")}
              className={`px-3 py-1.5 rounded uppercase tracking-wider border ${
                mode === "trainer"
                  ? "border-[var(--teal)] text-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
                  : "border-white/10 text-[var(--muted)] hover:text-[var(--bone)]"
              }`}
              data-testid="battle-mode-trainer"
            >
              Trainer
            </button>
          </div>
        </div>

        <div>
          <div className="label mb-1">Your Pal</div>
          <PalPicker value={palName} onChange={setPalName} pals={pals} testid="battle-pal-picker" />
        </div>

        {mode === "trainer" && (
          <TrainerPicker
            trainers={trainers}
            value={trainerId}
            onChange={setTrainerId}
          />
        )}

        <button
          onClick={startBattle}
          disabled={busy || !palName || (mode === "trainer" && !trainerId) || cdRemainMs > 0}
          className="btn w-full"
          data-testid="battle-button"
        >
          {cdRemainMs > 0
            ? `Cooldown ${cdMin}:${String(cdSec).padStart(2, "0")}`
            : busy
              ? "Loading…"
              : mode === "trainer"
                ? "Challenge Trainer"
                : "Find a Wild Pal"}
        </button>

        <p className="text-[11px] text-[var(--muted)]">
          Battles are turn-based. Pick a skill each turn — type matchups and
          crits matter. Cooldown only kicks in when the fight ends.
        </p>
      </div>

      <div className="glass-card p-6 min-h-[260px] flex items-center justify-center">
        {palName ? (
          (() => {
            const meta = pals.find((p) => p.pal_name === palName);
            return (
              <div className="text-center w-full" data-testid="battle-preview">
                <PalPortrait name={palName} size="w-32 h-32 mx-auto mb-3" />
                <div className="text-xl mb-1.5 glow-teal">{palName}</div>
                {meta && (
                  <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs text-[var(--muted)]">Lv {meta.level}</span>
                    <RarityBadge rarity={meta.rarity} />
                    {meta.elements?.slice(0, 3).map((e) => (
                      <ElementChip key={e} element={e} size={12} showLabel={false} />
                    ))}
                  </div>
                )}
                <div className="text-xs text-[var(--muted)]">
                  {cdRemainMs > 0
                    ? "Resting — countdown in progress."
                    : mode === "trainer"
                      ? "Pick a trainer to challenge."
                      : "Ready for the wilds."}
                </div>
              </div>
            );
          })()
        ) : (
          <p className="text-[var(--muted)] text-sm">Pick a Pal to begin.</p>
        )}
      </div>
    </div>
  );
}

function TrainerPicker({ trainers, value, onChange }) {
  return (
    <div>
      <div className="label mb-1">Trainer</div>
      {(!trainers || trainers.length === 0) ? (
        <div className="text-xs text-[var(--muted)] glass-card p-3">
          No trainers in range. Level a pal to unlock the first encounter.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1" data-testid="trainer-list">
          {trainers.map((t) => {
            const active = value === t.id;
            return (
              <button
                key={t.id}
                onClick={() => !t.locked && onChange(t.id)}
                disabled={t.locked}
                data-testid={`trainer-${t.id}`}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  active
                    ? "border-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
                    : t.locked
                      ? "border-white/5 opacity-50 cursor-not-allowed"
                      : "border-white/10 hover:bg-white/5"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{t.faction}</div>
                    <div className="text-sm font-semibold truncate">{t.name}</div>
                    <div className="text-[10px] text-[var(--muted)] mt-0.5">
                      {t.pal} · Lv {t.level} · {t.tier}
                    </div>
                  </div>
                  <span className={`badge text-[10px] ${
                    t.locked
                      ? "bg-white/5 text-[var(--muted)] border-white/10"
                      : "bg-[rgba(255,215,128,0.10)] text-[var(--gold)] border-[var(--gold)]/30"
                  }`}>
                    {t.locked ? `Lv ${t.min_level}` : `+${t.reward_skulls.toLocaleString()}`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BattleArena({ battle, busy, onSkill, onExit, logRef }) {
  const won = battle.result?.won;
  return (
    <div className="max-w-5xl mx-auto fade-up" data-testid="battle-arena">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="text-xl" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            {battle.mode === "trainer" ? `Duel — ${battle.trainer?.name || "Trainer"}` : "Wild Encounter"}
          </h2>
          {battle.trainer?.blurb && (
            <p className="text-xs text-[var(--muted)] italic mt-0.5">"{battle.trainer.blurb}"</p>
          )}
        </div>
        {battle.finished && (
          <button onClick={onExit} className="btn btn-sm" data-testid="battle-exit">
            <ChevronLeft size={12} /> Done
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <BattleCombatant
          label="Your Pal"
          name={battle.player.name}
          level={battle.player.level}
          elements={battle.player.elements}
          hp={battle.player.hp}
          hpMax={battle.player.hp_max}
          isWinner={battle.finished && won}
        />
        <BattleCombatant
          label={battle.mode === "trainer" ? "Trainer's Pal" : "Wild Pal"}
          name={battle.opponent.name}
          level={battle.opponent.level}
          elements={battle.opponent.elements}
          hp={battle.opponent.hp}
          hpMax={battle.opponent.hp_max}
          rarity={battle.opponent.rarity}
          isLoser={battle.finished && won}
          accent="danger"
        />
      </div>

      <div className="glass-card p-4 mb-4">
        <h4 className="label">Skills</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(battle.player.skills || []).map((s, idx) => (
            <button
              key={idx}
              onClick={() => onSkill(idx)}
              disabled={busy || battle.finished}
              className="btn btn-sm"
              data-testid={`battle-skill-${idx}`}
            >
              <Zap size={12} /> {s.name}
              <span className="text-[10px] ml-1 opacity-70">{s.power} pwr</span>
            </button>
          ))}
        </div>
      </div>

      <div
        className="glass-card p-4"
        ref={logRef}
        style={{ maxHeight: 260, overflowY: "auto" }}
        data-testid="battle-log"
      >
        <h4 className="label">Combat Log</h4>
        {(battle.log || []).length === 0 && (
          <div className="text-xs text-[var(--muted)]">Pick a skill to begin the battle.</div>
        )}
        <ul className="space-y-1 text-xs">
          {(battle.log || []).map((e, i) => (
            <li key={i} className={e.actor === "player" ? "text-[var(--teal)]" : "text-rose-300"}>
              <span className="text-[var(--muted)] mr-1">T{e.turn}</span>
              <span className="font-semibold">{e.actor === "player" ? "You" : "Opponent"}</span>{" "}
              used <strong>{e.skill}</strong> for{" "}
              <span className="glow-gold">{e.damage}</span>
              {e.crit ? <span className="text-amber-300"> · CRIT</span> : null}
              {e.effectiveness === "super_effective" && <span className="text-emerald-300"> · super effective!</span>}
              {e.effectiveness === "not_effective" && <span className="text-[var(--muted)]"> · not very effective</span>}
            </li>
          ))}
        </ul>
      </div>

      {battle.finished && battle.result && (
        <div
          className={`mt-4 glass-card p-6 text-center ${
            battle.result.won ? "border border-[var(--teal)]/40" : "border border-rose-500/30"
          }`}
          data-testid="battle-result"
        >
          {battle.result.won ? (
            <>
              <Trophy size={36} className="text-amber-300 mx-auto mb-2" />
              <h3 className="text-2xl" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
                {battle.mode === "trainer" ? `${battle.trainer?.name || "Trainer"} Defeated` : "Victory"}
              </h3>
              <p className="text-sm mt-2">
                <span className="glow-gold inline-flex items-center gap-1">
                  +{(battle.result.reward || 0).toLocaleString()} <ChilletCoinIcon size={14}/>
                </span>
                {" "}·{" "}
                <span className="text-amber-300">+{(battle.result.xp_gained || 0).toLocaleString()} XP</span>
              </p>
            </>
          ) : (
            <>
              <ChilletSkull size={36} className="text-rose-400 mx-auto mb-2" />
              <h3 className="text-2xl" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
                Your Pal Was Defeated
              </h3>
              <p className="text-xs text-[var(--muted)] mt-2">
                No cooldown — pick another pal and try again.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BattleCombatant({ label, name, level, elements, hp, hpMax, rarity, isWinner, isLoser, accent }) {
  const pct = Math.max(0, Math.min(100, Math.round((hp / Math.max(1, hpMax)) * 100)));
  const colour = pct > 60 ? "bg-emerald-500" : pct > 30 ? "bg-amber-400" : "bg-rose-500";
  const prevHpRef = useRef(hp);
  const [hit, setHit] = useState(false);
  useEffect(() => {
    if (hp < prevHpRef.current) {
      setHit(true);
      const t = setTimeout(() => setHit(false), 400);
      prevHpRef.current = hp;
      return () => clearTimeout(t);
    }
    prevHpRef.current = hp;
  }, [hp]);

  return (
    <div
      className={`glass-card p-4 transition-opacity ${isLoser ? "opacity-50" : ""} ${isWinner ? "ring-1 ring-[var(--teal)]/40" : ""} ${hit ? "combatant--hit" : ""}`}
      data-testid={`battle-combatant-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center bg-black/40 rounded-lg overflow-hidden">
          <PalPortrait name={name} size="w-20 h-20" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{label}</div>
          <div className="text-base font-semibold truncate">{name}</div>
          <div className="text-xs text-[var(--muted)]">
            Lv {level}
            {rarity ? <> · <span className="text-amber-300">{rarity}</span></> : null}
            {elements?.length ? <> · {elements.join(" / ")}</> : null}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Heart size={12} className={accent === "danger" ? "text-rose-400" : "text-emerald-400"} />
        <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
          <div
            className={`h-full ${colour} transition-all duration-500`}
            style={{ width: `${pct}%` }}
            data-testid="battle-hp-bar"
          />
        </div>
        <span className={`font-mono text-[10px] ${hit ? "text-rose-300" : ""}`}>{hp}/{hpMax}</span>
      </div>
    </div>
  );
}

